import { processImage, blobToFile } from "./imageUtils";
import type { OrderStatus, UserRole } from "./types";

type ApiId = string | number;
type ApiRecord = Record<string, unknown>;

interface RegisterInput {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  role?: UserRole | string;
}

interface CategoryInput {
  name: string;
  nameEs?: string;
  nameHe?: string;
  slug?: string;
  imageUrl?: string;
  parentId?: ApiId | null;
  level?: number;
  sortOrder?: number;
  isActive?: boolean;
}

const API_BASE = import.meta.env.VITE_API_BASE;

// Helper function to get CSRF token from cookie
function getCsrfToken(): string | null {
  const cookies = document.cookie.split(";");
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split("=");
    if (name === "csrf_token") {
      return decodeURIComponent(value);
    }
  }
  return null;
}

export function hasAuthSession(): boolean {
  return Boolean(getCsrfToken());
}

// Helper function to add CSRF header to requests
function getHeaders(
  additionalHeaders: Record<string, string> = {},
): HeadersInit {
  const headers: HeadersInit = {
    "Content-Type": "application/json",
    ...additionalHeaders,
  };

  // Add CSRF token for state-changing requests
  const csrfToken = getCsrfToken();
  if (csrfToken) {
    headers["X-CSRF-Token"] = csrfToken;
  }

  return headers;
}

export async function apiLogin(email: string, password: string) {
  const res = await fetch(`${API_BASE}/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
    credentials: "include",
  });
  if (!res.ok) throw new Error("Login failed");
  return res.json();
}

export async function apiRegister(userData: RegisterInput) {
  const res = await fetch(`${API_BASE}/register`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      email: userData.email,
      password: userData.password,
      name: `${userData.firstName} ${userData.lastName}`,
      role: userData.role || "client",
    }),
    credentials: "include",
  });
  if (!res.ok) throw new Error("Registration failed");
  return res.json();
}

export async function apiLogout() {
  const res = await fetch(`${API_BASE}/logout`, {
    method: "POST",
    headers: getHeaders(),
    credentials: "include",
  });
  if (!res.ok) throw new Error("Logout failed");
  return res.json();
}

export async function apiGetCurrentUser() {
  const res = await fetch(`${API_BASE}/auth/me`, {
    credentials: "include",
  });
  if (!res.ok) {
    if (res.status === 401) {
      return { success: false, message: "Not authenticated" };
    }
    throw new Error("Failed to get current user");
  }
  return res.json();
}

export interface ProductsParams {
  page?: number;
  per_page?: number;
  category_id?: string | number;
  search?: string;
  sort_by?: string;
  sort_order?: string;
  include_inactive?: boolean;
  language?: string;
}

export async function apiGetProducts(params: ProductsParams = {}) {
  const {
    page = 1,
    per_page = 20,
    category_id,
    search = "",
    sort_by = "name",
    sort_order = "asc",
    include_inactive = false,
    language = "en",
  } = params;

  const queryParams = new URLSearchParams({
    page: page.toString(),
    per_page: per_page.toString(),
    sort_by,
    sort_order,
    language,
  });

  if (category_id) {
    queryParams.append("category_id", category_id.toString());
  }

  if (search) {
    queryParams.append("search", search);
  }

  if (include_inactive) {
    queryParams.append("include_inactive", "true");
  }

  const res = await fetch(`${API_BASE}/products?${queryParams}`);
  if (!res.ok) throw new Error("Failed to fetch products");
  return res.json();
}

// Legacy function for backward compatibility
export async function apiGetProductsLegacy() {
  const res = await fetch(`${API_BASE}/products?page=1&per_page=1000`);
  if (!res.ok) throw new Error("Failed to fetch products");
  const data = await res.json();
  return data.products || data; // Handle both old and new format
}

export async function apiGetProductsByIds(productIds: number[]) {
  if (productIds.length === 0) return [];
  const promises = productIds.map((id) =>
    fetch(`${API_BASE}/products/${id}`)
      .then((res) => (res.ok ? res.json() : null))
      .catch(() => null),
  );
  const results = await Promise.all(promises);
  return results.filter(Boolean);
}

export async function apiGetProduct(productId: ApiId) {
  const res = await fetch(`${API_BASE}/products/${productId}`);
  if (!res.ok) throw new Error("Failed to fetch product");
  return res.json();
}

export async function apiGetProductById(
  productId: string,
  userId: number | null,
) {
  const params = new URLSearchParams();
  if (userId) params.append("user_id", userId.toString());

  const url = `${API_BASE}/products/${productId}${
    params.toString() ? `?${params.toString()}` : ""
  }`;

  const res = await fetch(url);
  if (!res.ok) throw new Error("Failed to fetch product");
  return res.json();
}

export async function prefetchProductById(
  productId: ApiId,
  userId: ApiId | null = null,
): Promise<void> {
  const params = new URLSearchParams();
  if (userId) params.append("user_id", userId.toString());

  const url = `${API_BASE}/products/${productId}${
    params.toString() ? `?${params.toString()}` : ""
  }`;

  try {
    await fetch(url, {
      credentials: "include",
      cache: "force-cache",
    });
  } catch {
    // Prefetch should be best-effort and silent.
  }
}

export async function apiGetCart(userId: ApiId) {
  const res = await fetch(`${API_BASE}/cart/${userId}`);
  if (!res.ok) throw new Error("Failed to fetch cart");
  return res.json();
}

export async function apiAddToCart(
  userId: ApiId,
  productId: ApiId,
  quantity: number,
) {
  const res = await fetch(`${API_BASE}/cart/${userId}`, {
    method: "POST",
    headers: getHeaders(),
    credentials: "include",
    body: JSON.stringify({ product_id: productId, quantity }),
  });
  if (!res.ok) throw new Error("Failed to add to cart");
  return res.json();
}

export async function apiRemoveFromCart(userId: ApiId, productId: ApiId) {
  const res = await fetch(`${API_BASE}/cart/${userId}/${productId}`, {
    method: "DELETE",
    headers: getHeaders(),
    credentials: "include",
  });
  if (!res.ok) throw new Error("Failed to remove from cart");
  return res.json();
}

export async function apiUpdateCartQuantity(
  userId: ApiId,
  productId: ApiId,
  quantity: number,
) {
  const res = await fetch(`${API_BASE}/cart/${userId}/${productId}`, {
    method: "PUT",
    headers: getHeaders(),
    credentials: "include",
    body: JSON.stringify({ quantity }),
  });
  if (!res.ok) throw new Error("Failed to update cart quantity");
  return res.json();
}

export async function apiCheckout(userId: ApiId, checkoutData: ApiRecord = {}) {
  const res = await fetch(`${API_BASE}/checkout/${userId}`, {
    method: "POST",
    headers: getHeaders(),
    credentials: "include",
    body: JSON.stringify(checkoutData),
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.message || "Checkout failed");
  }
  return res.json();
}

export async function apiGuestCheckout(checkoutData: ApiRecord) {
  const res = await fetch(`${API_BASE}/checkout/guest`, {
    method: "POST",
    headers: getHeaders(),
    credentials: "include",
    body: JSON.stringify(checkoutData),
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.message || "Checkout failed");
  }
  return res.json();
}

export async function apiGetOrders(userId: ApiId) {
  const res = await fetch(`${API_BASE}/orders/${userId}`);
  if (!res.ok) throw new Error("Failed to fetch orders");
  return res.json();
}

// Product management APIs
export async function apiCreateProduct(productData: ApiRecord) {
  const res = await fetch(`${API_BASE}/products`, {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify(productData),
    credentials: "include",
  });
  if (!res.ok) throw new Error("Failed to create product");
  return res.json();
}

export async function apiUpdateProduct(
  productId: ApiId,
  productData: ApiRecord,
) {
  const res = await fetch(`${API_BASE}/products/${productId}`, {
    method: "PUT",
    headers: getHeaders(),
    body: JSON.stringify(productData),
    credentials: "include",
  });
  if (!res.ok) throw new Error("Failed to update product");
  return res.json();
}

export async function apiDeleteProduct(productId: ApiId) {
  const res = await fetch(`${API_BASE}/products/${productId}`, {
    method: "DELETE",
    headers: getHeaders(),
    credentials: "include",
  });
  if (!res.ok) throw new Error("Failed to delete product");
  return res.json();
}

// Admin APIs
export async function apiGetAllOrders() {
  const res = await fetch(`${API_BASE}/admin/orders`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error("Failed to fetch all orders");
  return res.json();
}

export async function apiUpdateOrderStatus(
  orderId: ApiId,
  status: OrderStatus | string,
) {
  const res = await fetch(`${API_BASE}/admin/orders/${orderId}/status`, {
    method: "PUT",
    headers: getHeaders(),
    body: JSON.stringify({ status }),
    credentials: "include",
  });
  if (!res.ok) throw new Error("Failed to update order status");
  return res.json();
}

export async function apiClearCart(userId: ApiId) {
  const res = await fetch(`${API_BASE}/cart/${userId}/clear`, {
    method: "DELETE",
    headers: getHeaders(),
    credentials: "include",
  });
  if (!res.ok) throw new Error("Failed to clear cart");
  return res.json();
}

// Category APIs
export interface CategoriesParams {
  page?: number;
  per_page?: number;
  search?: string;
  paginated?: boolean;
  include_inactive?: boolean;
}

export async function apiGetCategoriesByIds(categoryIds: number[]) {
  if (categoryIds.length === 0) return [];
  const promises = categoryIds.map((id) =>
    fetch(`${API_BASE}/categories/${id}`)
      .then((res) => (res.ok ? res.json() : null))
      .catch(() => null),
  );
  const results = await Promise.all(promises);
  return results.filter(Boolean);
}

export async function apiGetCategories(params: CategoriesParams = {}) {
  const {
    page = 1,
    per_page = 20,
    search = "",
    paginated = false,
    include_inactive = false,
  } = params;

  const queryParams = new URLSearchParams();

  if (paginated || page > 1) {
    queryParams.append("page", page.toString());
    queryParams.append("per_page", per_page.toString());
    queryParams.append("paginated", "true");
  }

  if (search) {
    queryParams.append("search", search);
  }

  if (include_inactive) {
    queryParams.append("include_inactive", "true");
  }

  const res = await fetch(`${API_BASE}/categories?${queryParams}`);
  if (!res.ok) throw new Error("Failed to fetch categories");
  return res.json();
}

export async function apiCreateCategory(categoryData: CategoryInput) {
  // Convert camelCase to snake_case for backend
  const backendData = {
    name: categoryData.name,
    name_es: categoryData.nameEs,
    name_he: categoryData.nameHe,
    slug: categoryData.slug,
    image_url: categoryData.imageUrl,
    parent_id: categoryData.parentId,
    level: categoryData.level,
    sort_order: categoryData.sortOrder,
    is_active: categoryData.isActive ?? true,
  };

  const res = await fetch(`${API_BASE}/categories`, {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify(backendData),
    credentials: "include",
  });
  if (!res.ok) throw new Error("Failed to create category");
  return res.json();
}

export async function apiUpdateCategory(
  categoryId: ApiId,
  categoryData: CategoryInput,
) {
  // Convert camelCase to snake_case for backend
  const backendData: ApiRecord = {
    name: categoryData.name,
    name_es: categoryData.nameEs,
    name_he: categoryData.nameHe,
    slug: categoryData.slug,
    parent_id: categoryData.parentId,
    level: categoryData.level,
    sort_order: categoryData.sortOrder,
    is_active: categoryData.isActive ?? true,
  };

  // Only include image_url if it has a value (don't send empty string)
  if (categoryData.imageUrl) {
    backendData.image_url = categoryData.imageUrl;
  }

  const res = await fetch(`${API_BASE}/categories/${categoryId}`, {
    method: "PUT",
    headers: getHeaders(),
    body: JSON.stringify(backendData),
    credentials: "include",
  });
  if (!res.ok) throw new Error("Failed to update category");
  return res.json();
}

export async function apiDeleteCategory(categoryId: ApiId) {
  const res = await fetch(`${API_BASE}/categories/${categoryId}`, {
    method: "DELETE",
    headers: getHeaders(),
    credentials: "include",
  });
  if (!res.ok) throw new Error("Failed to delete category");
  return res.json();
}

// Admin user management APIs
export async function apiGetAllUsers() {
  const res = await fetch(`${API_BASE}/admin/users`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error("Failed to fetch users");
  return res.json();
}

export async function apiUpdateUserCashPayment(
  userId: ApiId,
  cashPaymentEnabled: boolean,
) {
  const res = await fetch(`${API_BASE}/admin/users/${userId}/cash-payment`, {
    method: "PUT",
    headers: getHeaders(),
    body: JSON.stringify({ cashPaymentEnabled }),
    credentials: "include",
  });
  if (!res.ok) throw new Error("Failed to update user cash payment setting");
  return res.json();
}

export async function apiUpdateUserRole(
  userId: ApiId,
  role: UserRole | string,
) {
  const res = await fetch(`${API_BASE}/admin/users/${userId}/role`, {
    method: "PUT",
    headers: getHeaders(),
    body: JSON.stringify({ role }),
    credentials: "include",
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.error || "Failed to update user role");
  }
  return res.json();
}

// Discount APIs
export async function apiGetDiscounts() {
  const res = await fetch(`${API_BASE}/discounts`);
  if (!res.ok) throw new Error("Failed to fetch discounts");
  return res.json();
}

export async function apiValidateDiscount(
  code: string,
  cartItems: unknown[],
  orderTotal: number,
  userId: string | null = null,
) {
  const res = await fetch(`${API_BASE}/discounts/validate`, {
    method: "POST",
    headers: getHeaders(),
    credentials: "include",
    body: JSON.stringify({
      code,
      cartItems,
      orderTotal,
      userId,
    }),
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.message || "Failed to validate discount");
  }
  return res.json();
}

// Admin discount management APIs
export async function apiGetAllDiscounts() {
  const res = await fetch(`${API_BASE}/admin/discounts`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error("Failed to fetch discounts");
  return res.json();
}

export async function apiCreateDiscount(discountData: ApiRecord) {
  const res = await fetch(`${API_BASE}/admin/discounts`, {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify(discountData),
    credentials: "include",
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.message || "Failed to create discount");
  }
  return res.json();
}

export async function apiUpdateDiscount(
  discountId: ApiId,
  discountData: ApiRecord,
) {
  const res = await fetch(`${API_BASE}/admin/discounts/${discountId}`, {
    method: "PUT",
    headers: getHeaders(),
    body: JSON.stringify(discountData),
    credentials: "include",
  });
  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.message || "Failed to update discount");
  }
  return res.json();
}

export async function apiDeleteDiscount(discountId: ApiId) {
  const res = await fetch(`${API_BASE}/admin/discounts/${discountId}`, {
    method: "DELETE",
    headers: getHeaders(),
    credentials: "include",
  });
  if (!res.ok) throw new Error("Failed to delete discount");
  return res.json();
}

export async function apiGetDiscountUsage(discountId: ApiId) {
  const res = await fetch(`${API_BASE}/admin/discounts/${discountId}/usage`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error("Failed to fetch discount usage");
  return res.json();
}

// Image Upload
// Helper function to convert image to webp and compress
async function convertToWebP(file: File): Promise<File> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      // Create canvas and draw image
      const canvas = document.createElement("canvas");

      // Resize if too large (max 2048px on longest side)
      let width = img.width;
      let height = img.height;
      const maxSize = 2048;

      if (width > maxSize || height > maxSize) {
        if (width > height) {
          height = (height / width) * maxSize;
          width = maxSize;
        } else {
          width = (width / height) * maxSize;
          height = maxSize;
        }
      }

      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");

      if (!ctx) {
        reject(new Error("Failed to get canvas context"));
        return;
      }

      ctx.drawImage(img, 0, 0, width, height);
      URL.revokeObjectURL(url);

      // Convert to webp with compression
      canvas.toBlob(
        (blob) => {
          if (blob) {
            // Convert blob to File
            const webpFile = new File(
              [blob],
              file.name.replace(/\.[^.]+$/, ".webp"),
              {
                type: "image/webp",
                lastModified: Date.now(),
              },
            );
            resolve(webpFile);
          } else {
            reject(new Error("Failed to convert image to webp"));
          }
        },
        "image/webp",
        0.85, // Quality 85% for good balance of size/quality
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Failed to load image"));
    };

    img.src = url;
  });
}

export async function apiUploadProductImage(file: File, productId?: number) {
  try {
    // Step 1: Process image (resize and convert to WebP)
    const processedBlob = await processImage(file, {
      maxWidth: 1200,
      maxHeight: 1200,
      quality: 0.85,
      format: "webp",
    });

    // Convert blob to file with .webp extension
    const filename = file.name.replace(/\.[^/.]+$/, ".webp");
    const processedFile = blobToFile(processedBlob, filename);

    // Step 2: Get presigned URL from backend
    const url = new URL(`${API_BASE}/upload/presigned-url`);
    url.searchParams.append("filename", processedFile.name);
    url.searchParams.append("folder", "products");
    if (productId) {
      url.searchParams.append("item_id", productId.toString());
    }

    const presignedRes = await fetch(url.toString(), {
      credentials: "include",
    });

    if (!presignedRes.ok) {
      throw new Error("Failed to get upload URL");
    }

    const { upload_url, public_url } = await presignedRes.json();

    // Step 3: Upload directly to S3 using presigned URL
    const uploadRes = await fetch(upload_url, {
      method: "PUT",
      body: processedFile,
      headers: {
        "Content-Type": "image/webp",
        "x-amz-server-side-encryption": "AES256",
      },
    });

    if (!uploadRes.ok) {
      throw new Error(
        `Upload failed: ${uploadRes.status} ${uploadRes.statusText}`,
      );
    }

    return { image_url: public_url };
  } catch (error) {
    console.error("Upload error:", error);
    throw error;
  }
}

export async function apiUploadCategoryImage(file: File, categoryId?: number) {
  try {
    // Step 1: Process image (resize and convert to WebP)
    const processedBlob = await processImage(file, {
      maxWidth: 800,
      maxHeight: 800,
      quality: 0.85,
      format: "webp",
    });

    // Convert blob to file with .webp extension
    const filename = file.name.replace(/\.[^/.]+$/, ".webp");
    const processedFile = blobToFile(processedBlob, filename);

    // Step 2: Get presigned URL from backend
    const url = new URL(`${API_BASE}/upload/presigned-url`);
    url.searchParams.append("filename", processedFile.name);
    url.searchParams.append("folder", "categories");
    if (categoryId) {
      url.searchParams.append("item_id", categoryId.toString());
    }

    const presignedRes = await fetch(url.toString(), {
      credentials: "include",
    });

    if (!presignedRes.ok) {
      throw new Error("Failed to get upload URL");
    }

    const { upload_url, public_url } = await presignedRes.json();

    // Step 3: Upload directly to S3 using presigned URL
    const uploadRes = await fetch(upload_url, {
      method: "PUT",
      body: processedFile,
      headers: {
        "Content-Type": "image/webp",
        "x-amz-server-side-encryption": "AES256",
      },
    });

    if (!uploadRes.ok) {
      throw new Error(
        `Upload failed: ${uploadRes.status} ${uploadRes.statusText}`,
      );
    }

    return { image_url: public_url };
  } catch (error) {
    console.error("Upload error:", error);
    throw error;
  }
}
